import {observer} from 'mobx-react';
import {Scrollbar} from 'react-scrollbars-custom';
import cn from 'classnames';
import {useMemo, useCallback, useState, useEffect} from 'react';

import useStores from 'src/hooks/useStores';
import {useLocale} from 'src/providers/LocaleProvider';
import {usePreciesTimer} from 'src/hooks/usePreciseTimer';
import {getTimeAndIncrement} from 'src/store/libs';
import useOrientation from 'src/hooks/useOrientation';
import useWindowSize from 'src/hooks/useWindowSize';
import {MAX_XL_TABLET} from 'src/pages/game-new2/constants';
import defaultPlaceholder from 'src/assets/images/defaultPlaceholder.png';
import {Switch} from 'src/shared/ui/Switch';

import HeaderBlock from '../LobbyHeader';
import {FilterButton} from '../FilterButton';
import {GameItem} from '../GameItem';

import {useStyles} from './styles';
import {GameTimeValues} from 'src/store/lobbyStore';
import LobbyActionMobile from 'src/pages/lobby/components/LobbyTable/HeaderBlock/HeaderMobile/index';
import GameAndInviteFilter, { GameInviteFilters } from '../GameFilter';
import { useGameFilters } from '../GameFilter/useGameFilters';

interface LobbyProps {}

const LobbyTable = ({}: LobbyProps) => {
    const classes = useStyles();
    const {lobbyStore, authStore} = useStores();

    const {width, height} = useWindowSize();
    const orientation = useOrientation();
    const {
        common: {newGame},
        header: {lobby, tournament},
        lobby: {
            createGame: {
                buttons: {create, match, potLimit},
            },
        },
        profile: {
            rankSystem: { allRanks, games, rakeBack, bonus, free },
        },
    } = useLocale();

    const {gameStore} = useStores();
    const isMobileSize = width < MAX_XL_TABLET;
    const me = authStore.currentUser;
    const allLobbyInviteGames = [...lobbyStore.allLobbyInviteGames].sort(
        (a, b) => {
            if (a.from._id === me?._id) return -1;
            if (b.from._id === me?._id) return 1;
            return 0;
        }
    );
    const allLobbyGames = [...lobbyStore.gamesAgregator].sort((a, b) => {
        if (a.players[0]._id === me?._id || a.players[1]._id === me?._id)
            return -1;
        if (b.players[0]._id === me?._id || b.players[1]._id === me?._id)
            return 1;
        return 0;
    });


    const isMatchMode = lobbyStore.matchConfig.isMatchMode;
    const isMatch = isMatchMode === 1 ? false : true;
    const isFreePlay = useMemo(() => {
        const isFree = document.location.href.indexOf('lobby-free-play') > 0;
        if (isFree) {
            lobbyStore.clearAllGames();
        }
        return isFree;
    }, []);

    const getTime = useCallback(
        (argument: GameTimeValues) => getTimeAndIncrement(argument).time,
        []
    );

    useEffect(() => {
        lobbyStore.filterGamesNew();
    }, [lobbyStore.allLobbyGames]);


    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState<GameInviteFilters>({
        bet: {},                
        time: {},               
        type: {},               
        gameMode: {},           
        matches: {},            
        showGames: true,        
        showInvites: true,      
    });


    const handleApplyFilter = (newFilters: GameInviteFilters) => {
        setFilters(newFilters);
        console.log('Применены фильтры:', newFilters);

    };

    const handleFilterReset = () => {
        const resetFilters: GameInviteFilters = {
            bet: {},
            time: {},
            type: {},
            gameMode: {},
            matches: {},
            showGames: true,
            showInvites: true
        };
        setFilters(resetFilters);
    };

    const filteredLobbyGames = useGameFilters(allLobbyGames, filters);
    const filteredInviteGames = useGameFilters(allLobbyInviteGames, filters);

    return (
        <div className={cn(classes.wrapper, {isMobile: isMobileSize})}>
            <div
                className={cn(classes.filterWrapper, {
                    isMobile: isMobileSize,
                })}
            >
                <h4
                    className={cn(classes.lobbyLabel, {
                        isMobile: isMobileSize,
                    })}
                >
                    {isFreePlay ? free('Free play') : games('Games')}
                </h4>
                {!isMobileSize && !isFreePlay && (
                    <Switch
                        value={isMatch}
                        onChange={() => {
                            lobbyStore.setMatchConfigValue(
                                !isMatch ? isMatchMode + 1 : isMatchMode - 1
                            );
                            lobbyStore.setMatchMode(!isMatch ? 2 : 1);
                        }}
                        leftLabel={potLimit('Pot limit')}
                        rightLabel={match('Match')}
                    />
                )}
                {!isMobileSize && !isFreePlay && (
                    <FilterButton onClick={() => setFilterOpen(true)} />

                )}
            </div>

            <GameAndInviteFilter
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                filters={filters}
                onApply={handleApplyFilter}
                onReset={handleFilterReset}  
            />

            {!isMobileSize && <HeaderBlock isMatch={isMatch} />}

            <Scrollbar className={cn(classes.gameListScroll, { isMobile: isMobileSize })} noScrollX={true}>
                <div className={classes.gameListWrapper}>

                    {filters.showInvites && !!filteredInviteGames &&
                        !!filteredInviteGames.length &&
                        filteredInviteGames.map((game, i) => (
                            <GameItem
                                key={game._id}
                                me={me}
                                id={game._id}
                                amount={game.details.bank}
                                rounds={game.details.gameCount}
                                time={getTime(game.time)}
                                type={game.details.fenType}
                                gameType={game.details.type}
                                createdAt={game.createdAt}
                                player1={{
                                    id: game?.from?._id,
                                    avatarUrl: game?.from?.avatar || defaultPlaceholder,
                                    nickname: game.from.nickname,
                                    stars: game.from.stars,
                                }}
                                player2={
                                    game?.to ? {
                                        id: game?.to?._id,
                                        avatarUrl: game?.to?.avatar || defaultPlaceholder,
                                        nickname: game.to.nickname,
                                        stars: game.to.stars,
                                    } : undefined
                                }
                                status={''}
                            />
                        ))
                    }

                    {filters.showGames && filteredLobbyGames &&
                        !!filteredLobbyGames.length &&
                        filteredLobbyGames.map((game, i) => (
                            <GameItem
                                key={game._id}
                                me={me}
                                id={game._id}
                                amount={game.bet}
                                rounds={game.matchCount}
                                type={game.fenType}
                                gameType={game.type}
                                time={getTime(game.time)}
                                createdAt={'123'}
                                player1={{
                                    id: game?.players[0]?._id || '123',
                                    avatarUrl: game?.players[0]?.avatar || defaultPlaceholder,
                                    nickname: game?.players[0]?.nickname || 'user',
                                    stars: game?.players[0]?.stars,
                                }}
                                player2={{
                                    id: !!game?.players[1]?._id ? game?.players[1]?._id : '123',
                                    avatarUrl: game?.players[1]?.avatar || defaultPlaceholder,
                                    nickname: game?.players[1]?.nickname || 'user',
                                    stars: game?.players[1]?.stars,
                                }}
                                status={''}
                            />
                        ))
                    }
                </div>
            </Scrollbar>
            {isMobileSize && (
                <LobbyActionMobile
                    filters={filters}
                    onApplyFilter={handleApplyFilter}
                    onResetFilter={handleFilterReset}
                />
            )}
        </div>
    );
};

export default observer(LobbyTable);
